import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import { CopyIcon, MoreVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import type { ApprovalChain } from '@/entities/approval-chain';
import { useTranslation } from 'react-i18next';

export interface ApprovalChainActionMenuProps {
  approvalChain: ApprovalChain;
  /** Gates every action behind project:manage-approval-chains. */
  canManage: boolean;
  onEdit: (approvalChain: ApprovalChain) => void;
  onDuplicate: (approvalChain: ApprovalChain) => void;
  onDelete: (approvalChain: ApprovalChain) => void;
}

export function ApprovalChainActionMenu({
  approvalChain,
  canManage,
  onEdit,
  onDuplicate,
  onDelete,
}: ApprovalChainActionMenuProps) {
  const { t } = useTranslation('approvalChains');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="mini"
          className="size-8 p-0"
          onClick={(event) => event.stopPropagation()}
          aria-label={t('manageApprovalChains.approvalChainActionMenu.approvalChainActions')}
          data-testid={`approval-chain-actions-${approvalChain.id}`}
        >
          <MoreVerticalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
        <DropdownMenuItem
          disabled={!canManage}
          onSelect={() => onEdit(approvalChain)}
          data-testid="approval-chain-action-edit"
        >
          <PencilIcon className="mr-2 size-4" />
          {t('manageApprovalChains.approvalChainActionMenu.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canManage}
          onSelect={() => onDuplicate(approvalChain)}
          data-testid="approval-chain-action-duplicate"
        >
          <CopyIcon className="mr-2 size-4" />
          {t('manageApprovalChains.approvalChainActionMenu.duplicate')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          disabled={!canManage}
          onSelect={() => onDelete(approvalChain)}
          data-testid="approval-chain-action-delete"
        >
          <Trash2Icon className="mr-2 size-4" />
          {t('manageApprovalChains.approvalChainActionMenu.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
